import { Component, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';

import { PublicLanguageService } from '../../../core/services/public-language.service';

/** The one storage key this banner owns. */
const STORAGE_NOTICE_KEY = 'legal.storageNoticeAcknowledged';

/**
 * Bottom notice about the cookie and the local storage the platform uses.
 *
 * It INFORMS instead of asking for consent, and that is a deliberate, factual
 * choice: there is no analytics, no advertising pixel and no third-party cookie
 * anywhere in this codebase. What the platform stores is the session (a HttpOnly
 * refresh cookie plus the session in local storage) and a handful of preferences
 * — all strictly necessary, none of it optional, so an Accept/Reject dialog would
 * be asking permission for something that cannot be turned off without breaking
 * sign-in.
 *
 * If a tracking or measurement tool is ever added, this component is where the
 * real consent (with categories and a reject path) has to be built, together with
 * the cookie policy it links to.
 *
 * Rendered once at the app root, next to the toast and the loading overlay, and
 * dismissed per browser.
 */
@Component({
  selector: 'app-storage-notice',
  standalone: true,
  imports: [RouterLink],
  styles: `
    .storage-notice {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 1500;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
      gap: 0.75rem 1.25rem;
      padding: 0.85rem clamp(1rem, 4vw, 2rem);
      background: #0d1b3d;
      color: rgb(226 232 240 / 0.92);
      box-shadow: 0 -8px 24px rgb(15 23 42 / 0.18);
      font-size: 0.84rem;
      line-height: 1.55;
      animation: storage-notice-in 220ms ease-out;
    }

    .storage-notice__text {
      margin: 0;
      max-width: 62rem;
    }

    .storage-notice__link {
      color: #93c5fd;
      font-weight: 600;
      text-decoration: underline;
    }

    .storage-notice__accept {
      flex-shrink: 0;
      padding: 0.45rem 1.1rem;
      border: none;
      border-radius: 0.6rem;
      background: #2563eb;
      color: #ffffff;
      font-size: 0.84rem;
      font-weight: 700;
      cursor: pointer;
      transition: background 0.18s ease;
    }

    .storage-notice__accept:hover {
      background: #1d4ed8;
    }

    @keyframes storage-notice-in {
      from {
        transform: translateY(100%);
      }
      to {
        transform: translateY(0);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .storage-notice {
        animation: none;
      }
    }

    /* A notice about browser storage means nothing on paper. */
    @media print {
      .storage-notice {
        display: none;
      }
    }
  `,
  template: `
    @if (isVisible()) {
      <aside class="storage-notice" role="region" [attr.aria-label]="copy().label">
        <p class="storage-notice__text">
          {{ copy().body }}
          <a class="storage-notice__link" routerLink="/legal/cookies">{{ copy().link }}</a>
        </p>
        <button type="button" class="storage-notice__accept" (click)="acknowledge()">{{ copy().accept }}</button>
      </aside>
    }
  `
})
export class StorageNoticeComponent {
  private readonly publicLanguage = inject(PublicLanguageService);

  protected readonly isVisible = signal(!this.wasAcknowledged());

  protected readonly copy = computed(() =>
    this.publicLanguage.language() === 'es'
      ? {
          label: 'Aviso de cookies y almacenamiento',
          body: 'Usamos una cookie técnica y el almacenamiento de su navegador para mantener su sesión y recordar su idioma y su tema. No usamos analítica, publicidad ni rastreo.',
          link: 'Ver la política',
          accept: 'Entendido'
        }
      : {
          label: 'Cookies and storage notice',
          body: 'We use one technical cookie and your browser storage to keep your session and remember your language and theme. We use no analytics, advertising or tracking.',
          link: 'Read the policy',
          accept: 'Got it'
        });

  protected acknowledge(): void {
    this.isVisible.set(false);

    try {
      localStorage.setItem(STORAGE_NOTICE_KEY, 'true');
    } catch {
      // Storage unavailable: the notice comes back next time, which is the honest
      // outcome — we could not record that it was seen.
    }
  }

  private wasAcknowledged(): boolean {
    try {
      return localStorage.getItem(STORAGE_NOTICE_KEY) === 'true';
    } catch {
      return false;
    }
  }
}
